'use client';

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Upload, Trash2, Image as ImageIcon, Save } from "lucide-react";
import { useOrg, errText } from "./internals";
import { useOrgSettings } from "./hooks/useOrgSettings";
import { Section, Field } from "./components/Field";
import { useToast } from "./components/Toaster";

const FIELDS = ["name", "tagline", "address", "city", "state", "pincode", "phone", "email", "website", "gstin", "pan"];
const blank = () => FIELDS.reduce((a, k) => ({ ...a, [k]: "" }), {});

export const OrgSettingsPage = () => {
  const { storage, currentUser, copy, onSaved, onCancel } = useOrg();
  const { org, loading, error, refresh } = useOrgSettings();
  const toast = useToast();
  const fileRef = useRef(null);

  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);

  const canEdit = ["owner", "admin"].includes(currentUser.role);
  const logoVersion = org?.profile?.logo?.updated_at;
  const logoUrl = logoVersion ? storage.logoUrl(logoVersion) : null;

  useEffect(() => {
    if (!org) return;
    const p = org.profile || {};
    setForm({
      name: org.name || "",
      tagline: p.tagline || "", address: p.address || "", city: p.city || "",
      state: p.state || "", pincode: p.pincode || "", phone: p.phone || "",
      email: p.email || "", website: p.website || "", gstin: p.gstin || "", pan: p.pan || "",
    });
    setLogoBroken(false);
  }, [org]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.show("Business name can't be empty.", "error");
      return;
    }
    setSaving(true);
    try {
      await storage.updateOrg(form);
      toast.show("Settings saved.", "success");
      await refresh();
      onSaved();
    } catch (e) {
      toast.show(errText(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const pickLogo = () => fileRef.current?.click();

  const onLogoChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.show("The logo must be under 3 MB.", "error");
      return;
    }
    setLogoBusy(true);
    try {
      await storage.uploadLogo(file);
      toast.show("Logo updated.", "success");
      await refresh();
      onSaved();
    } catch (err) {
      toast.show(errText(err), "error");
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await storage.deleteLogo();
      toast.show("Logo removed.", "success");
      await refresh();
      onSaved();
    } catch (err) {
      toast.show(errText(err), "error");
    } finally {
      setLogoBusy(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="org-loading" className="flex items-center justify-center px-6 py-24 text-sm text-zinc-500">
        <Loader2 size={16} className="mr-2 animate-spin" /> Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="org-error" className="mx-auto mt-8 max-w-2xl rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
        {error}
        <button type="button" onClick={refresh} className="ml-3 underline underline-offset-2">Try again</button>
      </div>
    );
  }

  return (
    <main data-testid="org-settings-page" className="mx-auto max-w-3xl px-4 py-6 pb-28 sm:px-6 sm:py-8">
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl">{copy.page_title}</h1>
        <p className="mt-1 text-sm text-zinc-500">{copy.page_subtitle}</p>
      </header>

      {!canEdit && (
        <div data-testid="org-readonly-note" className="mb-5 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Only an owner or admin can change these details. You can view them below.
        </div>
      )}

      <div className="space-y-5">
        {/* Logo */}
        <Section title="Company logo" subtitle="Shown at the top of the menu and on your business identity.">
          <div className="sm:col-span-2 flex items-center gap-5">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-zinc-200 bg-white">
              {logoUrl && !logoBroken ? (
                <img data-testid="org-logo-preview" src={logoUrl} alt="Company logo" onError={() => setLogoBroken(true)} className="h-full w-full object-contain p-2" />
              ) : (
                <ImageIcon size={28} className="text-zinc-300" />
              )}
            </div>
            {canEdit && (
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onLogoChosen} className="hidden" data-testid="org-logo-input" />
                <button
                  type="button"
                  data-testid="org-logo-upload-btn"
                  onClick={pickLogo}
                  disabled={logoBusy}
                  className="inline-flex items-center gap-2 rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
                >
                  {logoBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {logoUrl ? "Change logo" : "Upload logo"}
                </button>
                {logoUrl && !logoBroken && (
                  <button
                    type="button"
                    data-testid="org-logo-remove-btn"
                    onClick={removeLogo}
                    disabled={logoBusy}
                    className="inline-flex items-center gap-2 text-xs font-medium text-red-600 hover:underline disabled:opacity-60"
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                )}
                <span className="text-xs text-zinc-400">PNG, JPG, SVG or WEBP, up to 3 MB.</span>
              </div>
            )}
          </div>
        </Section>

        {/* Business */}
        <Section title="Business">
          <Field full label="Business name" value={form.name} onChange={set("name")} disabled={!canEdit} testId="org-name-input" placeholder="Your company name" />
          <Field full label="Tagline (optional)" value={form.tagline} onChange={set("tagline")} disabled={!canEdit} testId="org-tagline-input" placeholder="A short line about your business" />
        </Section>

        {/* Address & contact */}
        <Section title="Address & contact" subtitle="Your address is added automatically to your WhatsApp messages.">
          <Field full label="Address" value={form.address} onChange={set("address")} disabled={!canEdit} testId="org-address-input" textarea placeholder="Building, street, area" />
          <Field label="City" value={form.city} onChange={set("city")} disabled={!canEdit} testId="org-city-input" />
          <Field label="State" value={form.state} onChange={set("state")} disabled={!canEdit} testId="org-state-input" />
          <Field label="Pincode" value={form.pincode} onChange={set("pincode")} disabled={!canEdit} testId="org-pincode-input" inputMode="numeric" transform="digits" maxLength={6} />
          <Field label="Phone" value={form.phone} onChange={set("phone")} disabled={!canEdit} testId="org-phone-input" inputMode="numeric" transform="digits" maxLength={10} />
          <Field label="Email" value={form.email} onChange={set("email")} disabled={!canEdit} testId="org-email-input" type="email" placeholder="you@company.com" />
          <Field label="Website" value={form.website} onChange={set("website")} disabled={!canEdit} testId="org-website-input" placeholder="company.com" />
        </Section>

        {/* Legal & tax */}
        <Section title="Legal & tax">
          <Field label="GST number" value={form.gstin} onChange={set("gstin")} disabled={!canEdit} testId="org-gstin-input" transform="upper" maxLength={15} />
          <Field label="PAN" value={form.pan} onChange={set("pan")} disabled={!canEdit} testId="org-pan-input" transform="upper" maxLength={10} />
        </Section>
      </div>

      {canEdit && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-200 bg-white/90 px-4 py-3 backdrop-blur sm:left-64">
          <div className="mx-auto flex max-w-3xl justify-end gap-2">
            {onCancel && (
              <button
                type="button"
                data-testid="org-settings-cancel-btn"
                onClick={onCancel}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md border border-zinc-300 px-5 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              data-testid="org-settings-save-btn"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-green-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Save changes
            </button>
          </div>
        </div>
      )}
    </main>
  );
};
